"use client";

import React from "react";
import { Section } from "@/components/ui/section";
import { m as motion } from "framer-motion";
import { EDUCATION_DATA, SECTION_METADATA } from "@/lib/data";

const Education = React.memo(({ data }: { data?: any }) => {
  const educationData = data || EDUCATION_DATA;
  return (
    <Section id="education" className="py-16 md:py-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="mb-12 md:mb-20 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-[9px] md:text-xs font-mono uppercase tracking-[0.4em] text-muted-foreground mb-3 opacity-70"
          >
            {SECTION_METADATA.education.sectionNumber} /{" "}
            {SECTION_METADATA.education.sectionLabel}
          </motion.h2>
          <h3 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight px-2">
            {SECTION_METADATA.education.title}
          </h3>
        </div>

        <div className="space-y-12 md:space-y-16 relative before:absolute before:left-0 sm:before:left-0 before:top-2 before:bottom-2 before:w-[1.5px] before:bg-gradient-to-b before:from-primary/40 before:via-border/40 before:to-transparent max-w-4xl mx-auto">
          {educationData.map((edu: any, index: number) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative pl-7 sm:pl-12"
            >
              {/* Timeline Dot */}
              <div className="absolute left-[-4.5px] top-2 w-2.5 h-2.5 rounded-full bg-background border-2 border-primary shadow-[0_0_10px_rgba(var(--primary),0.5)] group-hover:scale-125 transition-all duration-500 z-10" />

              <div className="flex flex-col gap-2.5 md:gap-4">
                <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1.5 sm:gap-2">
                  <h4 className="text-base sm:text-xl md:text-2xl font-bold text-foreground group-hover:text-primary transition-colors duration-300 leading-tight">
                    {edu.school}
                  </h4>
                  <span className="text-[9px] sm:text-xs font-mono font-bold text-muted-foreground/40 tracking-[0.2em] uppercase shrink-0">
                    {edu.year}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                  <p className="text-xs sm:text-base md:text-lg text-primary/80 font-serif italic leading-relaxed">
                    {edu.degree}
                  </p>

                  {edu.grade && (
                    <div className="flex items-center justify-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-secondary/10 border border-border/40 backdrop-blur-md shadow-sm">
                      <span className="text-[10px] sm:text-xs font-mono leading-none text-muted-foreground/80">
                        <span className="hidden sm:inline">GPA: </span>
                        <span className="font-black text-foreground">
                          {edu.grade.replace("CGPA: ", "")}
                        </span>
                      </span>
                    </div>
                  )}
                </div>

                {edu.activities && edu.activities.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {edu.activities.map((act: string, idx: number) => (
                      <span
                        key={idx}
                        className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-muted-foreground/60 border border-border/20 px-3 py-1 rounded-full bg-secondary/5"
                      >
                        {act}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
});

Education.displayName = "Education";
export default Education;
